import React from "react";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";
import { MentorDesk } from "../illustrations/MentorDesk";

const PROOF = [
  "Milestone-bound roadmap — study at your natural pace with zero deadline pressure",
  "1:1 live sessions taught by practicing AI engineers & founders",
  "Real cloud compute provided: AWS credits & premium Google Colab subscriptions",
  "Strictly 50 candidates at a time, so every code review is personal",
];

export const Hero = ({ onRegister }) => {
  const reduced = useReducedMotion();

  return (
    <Box component="section" sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 4, md: 7 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div variants={stagger(0.09)} {...revealProps(reduced)}>
              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    display: "inline-flex",
                    px: 1.75,
                    py: 0.75,
                    mb: 3,
                    borderRadius: "999px",
                    backgroundColor: palette.primarySoft,
                    color: palette.primaryHover,
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: "0.02em",
                  }}
                >
                  1:1 AI Mentorship · Strictly 50 Candidates at a Time
                </Typography>
              </motion.div>

              {/* The page's only h1. */}
              <motion.div variants={fadeUp}>
                <Typography
                  component="h1"
                  sx={{
                    color: palette.text,
                    fontWeight: 700,
                    letterSpacing: "-0.035em",
                    lineHeight: 1.04,
                    fontSize: { xs: 39, sm: 50, md: 57 },
                  }}
                >
                  We teach AI,
                  <Box component="span" sx={{ color: palette.primary }}>
                    {" "}
                    one to one
                  </Box>
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    color: palette.textMuted,
                    fontSize: { xs: 17, md: 19 },
                    lineHeight: 1.68,
                    mt: 3,
                    maxWidth: 520,
                  }}
                >
                  From your first mathematical intuition to production AI engineering.
                  A plan built with you, bound to milestones — not an arbitrary cohort's calendar.
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 4.5 }}>
                  <Button
                    onClick={onRegister}
                    disableElevation
                    sx={{
                      px: 3.5,
                      py: 1.5,
                      borderRadius: "999px",
                      background: palette.ctaGradient,
                      color: "#ffffff",
                      fontSize: 16,
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": { background: palette.primaryHover },
                    }}
                  >
                    Start with a conversation
                  </Button>
                </Box>
              </motion.div>

              <motion.ul
                variants={fadeUp}
                style={{ listStyle: "none", padding: 0, margin: "36px 0 0" }}
              >
                {PROOF.map((item) => (
                  <Box
                    key={item}
                    component="li"
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 1.5,
                      color: palette.textMuted,
                      fontSize: 14.5,
                      lineHeight: 1.55,
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 6,
                        height: 6,
                        mt: "8px",
                        flexShrink: 0,
                        borderRadius: "50%",
                        backgroundColor: palette.primary,
                      }}
                    />
                    {item}
                  </Box>
                ))}
              </motion.ul>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <MentorDesk />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
